import React, { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import PlusIcon from '@mui/icons-material/Add';
import cs from 'classnames';

import { getAllGroupsPageSortOptionAndType, setAllGroupsPageSortOptionAndType } from '../../helpers';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getGroupsThunk } from '../../store/groups/groupsSlice';
import { PaginationFooter } from '../modules/PaginationFooter';
import UpdateParticipantModal from './UpdateParticipantModal';
import { SortOptions, SortType } from '../../enums/SORT';
import { GROUP_WITH_SUBGROUP } from '../../enums/TYPES';
import LinearProgress from '../modules/LinearProgress';
import { ContainedButton } from '../modules/Buttons';
import useDebounce from '../../helpers/useDebounce';
import { OrderSelector } from '../modules/Selector';
import DeleteGroupModal from './DeleteGroupModal';
import CreateGroupModal from './CreateGroupModal';
import SearchInput from '../modules/SearchInput';
import styles from './AnalystGroups.module.scss';
import GroupCard from '../modules/GroupCard';
import { STATUS } from '../../enums/ENUMS';
import { ROLES } from '../../enums/ROLES';
import Divider from '../modules/Divider';

const AnalystGroups = () => {
  const dispatch = useAppDispatch();

  const { role } = useAppSelector((state) => state.user);
  const { groups, status, total } = useAppSelector((state) => state.groupsStore);

  const [showModalEdit, setShowModalEdit] = useState(false);
  const [sortOption, setSortOption] = useState(SortOptions.NAME);
  const [sort, setSort] = useState(SortType.ASC);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOrgId, setOrgId] = useState<string>('');
  const [searchText, setSearch] = useState('');

  const debouncedValue = useDebounce<string>(searchText, 500);
  const [trigger, setTrigger] = useState(0);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    localStorage.setItem('groups_pagination_rows', e.target.value);
    setLimit(parseInt(e.target.value, 10));
    setPage(0);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearch(e.target.value);
  };

  const switchEditModal = () => {
    setShowModalEdit(!showModalEdit);
  };

  const switchCreateModal = () => {
    setShowCreateModal(!showCreateModal);
  };

  const switchDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const toDelete = (id: string) => {
    setOrgId(id);
    switchDeleteModal();
  };

  useEffect(() => {
    const paginationFromLocalStorage = localStorage.getItem('groups_pagination_rows');
    if (paginationFromLocalStorage) {
      setLimit(parseInt(paginationFromLocalStorage, 10));
    }
    const res = getAllGroupsPageSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      if (sortAndOption[0] && sortAndOption[1]) {
        setSort(sortAndOption[0] as SortType);
        setSortOption(sortAndOption[1] as SortOptions);
        dispatch(
          getGroupsThunk({
            search: searchText,
            sort: sortAndOption[0] as SortType,
            sortOption: sortAndOption[1] as SortOptions,
            page,
            limit,
          }),
        );
      }
    } else {
      dispatch(getGroupsThunk({ search: searchText, sort, sortOption, page, limit }));
    }
  }, [debouncedValue, sort, sortOption, page, limit, trigger]);

  const [groupToEdit, setGroupToEdit] = useState<GROUP_WITH_SUBGROUP>();
  const onEditSelect = (group: GROUP_WITH_SUBGROUP) => {
    setGroupToEdit(group);
    switchEditModal();
  };

  const sortHanlder = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    if (option) {
      setSort(option.order);
      setAllGroupsPageSortOptionAndType(option.order, option.value);
      // @ts-ignore
      setSortOption(option.value);
    } else {
      if (sortOption === singleValue) {
        const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
        setSort(sortT);
        setAllGroupsPageSortOptionAndType(sortT, singleValue);
      } else {
        setSort(SortType.ASC);
        setAllGroupsPageSortOptionAndType(SortType.ASC, singleValue);
      }
      setSortOption(singleValue);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.link}>
        <div className={styles.linkEl}>Groups Management</div>
      </div>
      <div className="mb-5">
        <Divider />
      </div>
      <div className={styles.mainContent}>
        <div className="mb-10">
          <div className={styles.titleContainer}>
            <div className={styles.innerTitle}>Groups Management</div>
            {role === ROLES.ORGANIZATION_ADMIN && (
              <ContainedButton style={{ display: 'flex', alignItems: 'center' }} onClick={switchCreateModal}>
                <PlusIcon style={{ marginRight: '8px' }} fontSize="small" /> <span>Create Group</span>
              </ContainedButton>
            )}
          </div>
          <div className={styles.toolbar}>
            <SearchInput onChange={onSearch} className={styles.search} />

            <div className={styles.toolbarRoles}>
              <div>
                <OrderSelector
                  title={'Sorting type'}
                  selectedValue={sortOption}
                  selectedOrder={sort}
                  options={[
                    { label: 'A-Z', value: SortOptions.NAME, order: SortType.ASC },
                    { label: 'Z-A', value: SortOptions.NAME, order: SortType.DESC },
                    { label: 'Last Edit ↓', value: SortOptions.UPDATED_AT, order: SortType.DESC },
                    { label: 'Last Edit ↑', value: SortOptions.UPDATED_AT, order: SortType.ASC },
                  ]}
                  onSelect={sortHanlder}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={cs('mb-5', styles.gridContainer)}>
          <div className={styles.groupHeader}>
            <div className={styles.title} onClick={() => sortHanlder(SortOptions.NAME)}>
              Name
              {sortOption === SortOptions.NAME && sort === SortType.ASC && (
                <>
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
                </>
              )}
              {sortOption === SortOptions.NAME && sort === SortType.DESC && (
                <>
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
                </>
              )}
            </div>
            <div className={styles.details}>Details</div>
            <div
              className={styles.created}
            >
              Created Date
            </div>
            <div
              className={styles.updated}
              onClick={() => sortHanlder(SortOptions.UPDATED_AT)}
            >
              Last Edit Date{' '}
              {sortOption === SortOptions.UPDATED_AT && sort === SortType.DESC && (
                <>
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
                </>
              )}
              {sortOption === SortOptions.UPDATED_AT && sort === SortType.ASC && (
                <>
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
                </>
              )}
            </div>

            {role === ROLES.ORGANIZATION_ADMIN && <div className={styles.action}>Actions</div>}
          </div>

          <div className={styles.groupList}>
            {status === STATUS.LOADING && <LinearProgress />}
            {status === STATUS.IDLE && groups.length === 0 && (
              <div className={styles.noFoldersContainer}>
                <div className={styles.noFoldersText}>No Groups</div>
                <div className="emptyStateHint">Click create group button to add your first group!</div>
              </div>
            )}
            {groups.map((group, i) => (
              <GroupCard
                toDelete={toDelete}
                onEdit={onEditSelect}
                data={group}
                key={`${i}_${group.id}_${Date.now()}`}
                setFetchTrigger={setTrigger}
              />
            ))}
          </div>
        </div>
      </div>
      <UpdateParticipantModal group={groupToEdit} switchModal={switchEditModal} open={showModalEdit} setFetchTrigger={setTrigger} />
      <CreateGroupModal groups={groups} open={showCreateModal} switchModal={switchCreateModal} setFetchTrigger={setTrigger} />
      <DeleteGroupModal id={deleteOrgId} switchModal={switchDeleteModal} open={showDeleteModal} setFetchTrigger={setTrigger} />
      <PaginationFooter total={total} page={page} rowsPerPage={limit} onRowsPerPageChange={onRowsChange} onPageChange={onPaginate} />
    </div>
  );
};

export default AnalystGroups;
